import { z } from 'zod';
import { uuidV7Schema } from '../uuid.js';
/**
 * Le cycle de vie d'un **lot** — une campagne de contrôles commandée par un client.
 *
 * ⚠️ **`status` sur une autre table qu'`Inquiry`.** M3 a longtemps écrit « un seul dans tout le
 * modèle » ; c'est la **règle** qui a été corrigée, pas la colonne — la renommer aurait créé un
 * second mot pour la même nature, ce que M3 existe justement pour empêcher. Depuis le 13/09/2026,
 * la fiche admet un second `status` sous trois conditions, et celui-ci les tient : il décrit le
 * cycle de vie de **sa** ligne (la campagne est ouverte, en cours ou close, pas un contrôle) ; il
 * ne se déduit d'aucun autre, un lot `in_progress` contenant des demandes à tous les statuts ; et
 * il pilote la fiche de lot. Les valeurs sont au diagramme
 * (`Lot`, `docs/diagrams/mcd-ghp.mermaid`), qui fait foi sur la forme.
 *
 * ⚠️ Comme `Inquiry.status`, **aucune garde ne contraint les transitions** (M4) : rien n'empêche
 * de rouvrir un lot clos, et c'est voulu — une campagne qu'un client relance est un cas courant.
 */
export const LOT_STATUSES = ['new', 'in_progress', 'closed'];
export const lotStatusSchema = z.enum(LOT_STATUSES);
/**
 * `Lot` — l'ensemble de lieux qu'un client confie à GHP en une fois : un lotissement, un
 * patrimoine de bailleur, une campagne systématique sur un quartier.
 *
 * **Un lot naît de l'import de demandes**, avec un code dérivé du fichier, et reste modifiable
 * depuis la fiche client (décision du 11/09/2026). Le câblage de l'import n'est pas de ce lot-ci :
 * la table, sa lecture et l'écran le sont.
 *
 * ⚠️ **`code` est unique par client, pas globalement** : deux communes numérotent leurs
 * campagnes chacune de leur côté, et « 2024-01 » chez l'une n'a rien à voir avec « 2024-01 » chez
 * l'autre. L'unicité globale reste portée par l'`id` (M1).
 *
 * ⚠️ **`city` est dénormalisée exprès** — le diagramme le dit dans son commentaire : afficher un
 * lot ne doit pas coûter une jointure `Site → Address → Municipality`. Elle est nullable là où le
 * diagramme ne le dit pas, pour la même raison que le contact d'un client : un lot existe avant
 * que ses sites soient rattachés, et refuser sa création faute de ville perdrait l'import (M21).
 */
export const lotSchema = z.object({
    id: uuidV7Schema,
    clientId: uuidV7Schema,
    code: z.string().trim().min(1),
    name: z.string().trim().min(1),
    city: z.string().trim().min(1).nullable(),
    /** Le nombre de logements annoncé par le client — une prévision, pas un décompte de sites. */
    unitCount: z.number().int().nullable(),
    /** Le début de la campagne, en date simple (`YYYY-MM-DD`) : personne n'en lit l'heure. */
    startDate: z.iso.date().nullable(),
    status: lotStatusSchema,
});
//# sourceMappingURL=lot.js.map